import * as React from "react"
import { useCreateInvitationForResourcesMutation } from "@modules/organization/contributors/queries.generated"
import { InvitationEmailResourceCreateInput } from "@modules/graphql/types"
import { useShowSuccessToast } from "gatsby-interface"
import { FormattedMessage, commonMessageTags } from "@modules/locales"
import { siteTransfer as text } from "@modules/locales/default.js"
import { WatchQueryFetchPolicy } from "apollo-client"
import {
  useRevokeInvitationMutation,
  useResendInvitationMutation,
} from "@modules/organization/mutations.generated"
import {
  useGetSiteTransferInvitationQuery,
  GetSiteTransferInvitationDocument,
} from "@modules/site/queries.generated"
import { useSiteDetailsQuery } from "@modules/site/shared/queries.generated"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import useTracker from "@modules/analytics/hooks/useTracker"
import { SegmentEventType } from "@modules/analytics"

type Options = {
  fetchPolicy?: WatchQueryFetchPolicy
  organizationId?: string
}

export function useSiteTransfer(id: string, options?: Options) {
  const { fetchPolicy = `cache-and-network`, organizationId = `` } =
    options || {}

  const { trackSegment } = useTracker()

  const showSuccessToast = useShowSuccessToast()
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [isRevokeModalOpen, setIsRevokeModalOpen] = React.useState(false)
  const [isResendModalOpen, setIsResendModalOpen] = React.useState(false)
  const [setErrorInModal, errorAlertInModal] = useTriggerErrorAlert()
  const [setErrorInCard, errorAlertInCard] = useTriggerErrorAlert()

  const { data: siteData, loading: siteLodaing } = useSiteDetailsQuery({
    variables: {
      id,
    },
    onError: setErrorInCard,
  })

  const {
    data: invitationData,
    loading: invitationLoading,
  } = useGetSiteTransferInvitationQuery({
    variables: {
      id,
    },
    fetchPolicy: fetchPolicy,
    onError: setErrorInCard,
  })

  const invitation = invitationData?.getSiteTransferInvitation

  const [
    createInvitation,
    { loading: creating, error: createError },
  ] = useCreateInvitationForResourcesMutation()

  const [
    revokeInvitation,
    { loading: revoking },
  ] = useRevokeInvitationMutation()

  const [
    resendInvitation,
    { loading: resending },
  ] = useResendInvitationMutation()

  const create = (invitations: InvitationEmailResourceCreateInput[]) => {
    if (!invitations) {
      return
    }

    return createInvitation({
      variables: {
        invitations,
      },
      refetchQueries: [
        {
          query: GetSiteTransferInvitationDocument,
          variables: {
            id,
          },
        },
      ],
    })
      .then(data => {
        const isSuccess =
          data?.data?.createInvitationForResources?.validation?.success
        const email =
          data?.data?.createInvitationForResources?.contributors?.[0]?.email

        if (isSuccess && email) {
          trackSegment({
            type: SegmentEventType.Track,
            event: `Site Transfer Created`,
            properties: {
              organizationId,
              siteId: id,
            },
          })

          setIsModalOpen(false)

          return showSuccessToast(
            <FormattedMessage<"email", "strong">
              values={{ email: email }}
              message={text.messages.siteTransferEmailSentTo}
              tags={commonMessageTags}
            />
          )
        }

        setErrorInModal({
          message:
            data?.data?.createInvitationForResources?.validation?.message || ``,
        })
      })
      .catch(err => {
        setErrorInModal(err)
      })
  }

  const resend = () => {
    if (!invitation?.id) {
      return
    }

    resendInvitation({
      variables: {
        id: invitation?.id,
      },
      refetchQueries: [
        {
          query: GetSiteTransferInvitationDocument,
          variables: {
            id,
          },
        },
      ],
    })
      .then(data => {
        setIsResendModalOpen(false)

        trackSegment({
          type: SegmentEventType.Track,
          event: `Site Transfer Invitation Resent`,
          properties: {
            organizationId,
            siteId: id,
          },
        })

        showSuccessToast(
          <FormattedMessage<"email", "strong">
            values={{
              email: invitation?.invitationEmail || ``,
            }}
            message={text.messages.siteTransferEmailResentTo}
            tags={commonMessageTags}
          />
        )
      })
      .catch(err => {
        setErrorInModal(err)
      })
  }

  const revoke = () => {
    if (!invitation?.id) {
      return
    }

    revokeInvitation({
      variables: {
        id: invitation?.id,
      },
      refetchQueries: [
        {
          query: GetSiteTransferInvitationDocument,
          variables: {
            id,
          },
        },
      ],
    })
      .then(data => {
        if (data?.data?.revokeInvitation?.success) {
          setIsRevokeModalOpen(false)

          trackSegment({
            type: SegmentEventType.Track,
            event: `Site Transfer Cancelled`,
            properties: {
              organizationId,
              siteId: id,
            },
          })

          return showSuccessToast(
            <FormattedMessage<"email", "strong">
              values={{
                email:
                  invitationData?.getSiteTransferInvitation?.invitationEmail ||
                  ``,
              }}
              message={text.messages.siteTransferCancelled}
              tags={commonMessageTags}
            />
          )
        }

        if (data?.data?.revokeInvitation?.message) {
          return setErrorInModal({
            message: data.data.revokeInvitation.message,
          })
        }
      })
      .catch(err => {
        setErrorInModal(err)
      })
  }

  return {
    modalIsOpen: isModalOpen,
    closeModal: () => setIsModalOpen(false),
    openModal: () => setIsModalOpen(true),
    revokeModalIsOpen: isRevokeModalOpen,
    closeRevokeModal: () => {
      setIsRevokeModalOpen(false)
      setErrorInModal(null)
    },
    openRevokeModal: () => setIsRevokeModalOpen(true),
    resendModalIsOpen: isResendModalOpen,
    openResendModal: () => setIsResendModalOpen(true),
    closeResendModal: () => {
      setIsResendModalOpen(false)
      setErrorInModal(null)
    },
    loading: invitationLoading || siteLodaing,
    expired: invitation?.expired,
    pending: invitation && !invitation.accepted && !invitation.expired,
    sentTo: invitation?.invitationEmail,
    create,
    creating,
    createError,
    resend,
    resending,
    revoke,
    revoking,
    transfered: siteData?.siteDetails?.parentOrganizationId,
    errorInModal: errorAlertInModal,
    errorInCard: errorAlertInCard,
  }
}
